<script setup>
import { computed } from 'vue';

/**
 * SzReactionBadge
 * Render pills agrupadas por emoji com contador e tooltip dos nomes dos reagentes.
 * Esperamos `reactions = [{ emoji, senderType, senderId, senderName, createdAt }]`
 */
const props = defineProps({
  reactions: {
    type: Array,
    required: true,
  },
  orientation: {
    type: String,
    default: 'left', // 'left' | 'right'
  },
  currentUserId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['remove']);

const grouped = computed(() => {
  const map = new Map();
  for (const r of props.reactions || []) {
    const emoji = (r.emoji || '').trim();
    if (!emoji) continue;
    if (!map.has(emoji)) {
      map.set(emoji, { emoji, count: 0, names: [], mine: false });
    }
    const slot = map.get(emoji);
    slot.count += 1;
    if (r.senderName) slot.names.push(r.senderName);
    const isMine =
      props.currentUserId != null &&
      r.senderType === 'user' &&
      Number(r.senderId) === Number(props.currentUserId);
    if (isMine) slot.mine = true;
  }
  return Array.from(map.values());
});

const alignClass = computed(() =>
  props.orientation === 'right' ? 'justify-end' : 'justify-start'
);

function onPillClick(g) {
  if (!g.mine) return;
  emit('remove', g.emoji);
}
</script>

<template>
  <div
    v-if="grouped.length"
    class="sz-reaction-badge-row flex gap-1 mt-1 flex-wrap"
    :class="alignClass"
  >
    <span
      v-for="g in grouped"
      :key="g.emoji"
      v-tooltip="g.mine ? 'Clique para remover' : (g.names.join(', ') || g.emoji)"
      class="sz-reaction-pill inline-flex items-center gap-1 px-1.5 h-5 rounded-full text-xs leading-none select-none"
      :class="{ 'sz-reaction-pill--mine': g.mine }"
      :role="g.mine ? 'button' : undefined"
      :tabindex="g.mine ? 0 : undefined"
      @click.stop="onPillClick(g)"
      @keydown.enter.stop="onPillClick(g)"
      @keydown.space.stop.prevent="onPillClick(g)"
    >
      <span class="sz-reaction-emoji">{{ g.emoji }}</span>
      <span v-if="g.count > 1" class="sz-reaction-count font-medium">{{ g.count }}</span>
    </span>
  </div>
</template>

<style lang="scss" scoped>
.sz-reaction-pill {
  background: var(--color-background-alt, rgba(127, 127, 127, 0.12));
  border: 1px solid rgba(127, 127, 127, 0.18);
  color: var(--color-text, #09090b);
  transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}
.sz-reaction-pill:hover {
  transform: translateY(-1px);
}
.sz-reaction-pill--mine {
  cursor: pointer;
  background: rgba(56, 139, 253, 0.14);
  border-color: rgba(56, 139, 253, 0.45);
}
.sz-reaction-pill--mine:hover {
  background: rgba(220, 53, 69, 0.14);
  border-color: rgba(220, 53, 69, 0.55);
}
.sz-reaction-emoji {
  font-size: 13px;
  line-height: 1;
}
.sz-reaction-count {
  font-size: 11px;
  opacity: 0.8;
}
/* dark mode rules moved to unscoped block below — :global() inside scoped scss
   was being miscompiled into bare .dark{...} which painted the whole app red */
</style>

<style lang="scss">
.dark .sz-reaction-pill {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  color: #fafafa;
}
.dark .sz-reaction-pill--mine {
  background: rgba(56, 139, 253, 0.22);
  border-color: rgba(56, 139, 253, 0.6);
}
.dark .sz-reaction-pill--mine:hover {
  background: rgba(220, 53, 69, 0.22);
  border-color: rgba(220, 53, 69, 0.7);
}
</style>
